import React, { useRef, useState } from "react";
import { Modal, Button, Alert, Spin, Input } from "antd";
import {
  AudioOutlined,
  LoadingOutlined,
  CloseOutlined,
  CheckOutlined,
  RedoOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { BASE_API_URL } from "../../utils/consts";
import {
  getPublicAvailability,
  createPublicReservation,
} from "../../utils/api/reservationsAPI";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ReservationPreview {
  guestName: string;
  guestPhone: string | null;
  date: string;
  time: string;
  numberOfGuests: number;
  note: string | null;
}

type VoiceState =
  | { status: "idle" }
  | { status: "listening"; transcript: string }
  | { status: "parsing"; transcript: string }
  | { status: "preview"; data: ReservationPreview }
  | { status: "submitting" }
  | { status: "success"; guestName: string; date: string; time: string }
  | { status: "error"; message: string };

type Lang = "bcms" | "sl" | "en";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
function useVoicePublicReservation(clientId: string) {
  const [state, setState] = useState<VoiceState>({ status: "idle" });
  const [lang, setLang] = useState<Lang>("bcms");
  const recognitionRef = useRef<any>(null);
  const transcriptRef = useRef("");
  const lastTranscriptRef = useRef(""); // persists after parse for usage logging

  const langCode = lang === "en" ? "en-US" : lang === "sl" ? "sl-SI" : "hr-HR";

  const startListening = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setState({
        status: "error",
        message:
          "Voice input is not supported in this browser. Please use Chrome or Safari.",
      });
      return;
    }

    transcriptRef.current = "";
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const rec = new SpeechRecognition();
    rec.lang = langCode;
    rec.continuous = !isIOS;
    rec.interimResults = !isIOS;
    rec.maxAlternatives = 1;
    recognitionRef.current = rec;
    setState({ status: "listening", transcript: "" });

    rec.onresult = (e: any) => {
      let final = "";
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t + " ";
        else interim += t;
      }
      if (final) transcriptRef.current += final;
      setState({
        status: "listening",
        transcript: (transcriptRef.current + interim).trim(),
      });
    };

    rec.onerror = (e: any) => {
      recognitionRef.current = null;
      if (e.error !== "aborted") {
        setState({
          status: "error",
          message: "Could not capture audio. Please try again.",
        });
      }
    };

    rec.onend = () => {
      recognitionRef.current = null;
      const t = transcriptRef.current.trim();
      if (t) parseTranscript(t);
      else setState({ status: "idle" });
    };

    rec.start();
  };

  const stopListening = () => recognitionRef.current?.stop();

  const parseTranscript = async (transcript: string) => {
    lastTranscriptRef.current = transcript;
    setState({ status: "parsing", transcript });
    try {
      const { data } = await axios.post(
        `${BASE_API_URL}/voice-assistant/query`,
        {
          transcript,
          clientId,
          currentDate: new Date().toISOString(),
        },
      );
      if (data.intent !== "reservation") {
        setState({
          status: "error",
          message:
            'Please describe a reservation. Example: "Reserve for John Smith tomorrow at 7pm for 4 people"',
        });
        return;
      }
      setState({ status: "preview", data: data.data as ReservationPreview });
    } catch (err: any) {
      setState({
        status: "error",
        message:
          err.response?.data?.message ||
          "Could not understand the command. Please try again.",
      });
    }
  };

  const confirmReservation = async (
    preview: ReservationPreview,
    phone: string,
    email?: string,
  ) => {
    setState({ status: "submitting" });
    try {
      // Validate time slot availability
      const avail = await getPublicAvailability(clientId, preview.date);
      if (!avail.data.available) {
        setState({
          status: "error",
          message: `This date is not available: ${avail.data.reason || "closed"}`,
        });
        return;
      }
      const slot = avail.data.slots?.find((s: any) => s.time === preview.time);
      if (!slot) {
        setState({
          status: "error",
          message: `Time slot ${preview.time} is not available. Please choose another time.`,
        });
        return;
      }
      if (!slot.available) {
        setState({
          status: "error",
          message: `Time slot ${preview.time} is fully booked. Please choose another time.`,
        });
        return;
      }

      await createPublicReservation({
        clientId,
        guestName: preview.guestName,
        guestPhone: phone,
        guestEmail: email || undefined,
        date: preview.date,
        time: preview.time,
        numberOfGuests: preview.numberOfGuests,
        note: preview.note || undefined,
        source: "voice",
        voiceTranscript: lastTranscriptRef.current || undefined,
      });
      setState({
        status: "success",
        guestName: preview.guestName,
        date: preview.date,
        time: preview.time,
      });
    } catch (err: any) {
      setState({
        status: "error",
        message:
          err.response?.data?.error || "Booking failed. Please try again.",
      });
    }
  };

  const reset = () => {
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    transcriptRef.current = "";
    setState({ status: "idle" });
  };

  return {
    state,
    lang,
    setLang,
    startListening,
    stopListening,
    confirmReservation,
    reset,
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
interface Props {
  clientId: string;
}

export default function VoicePublicReservation({ clientId }: Props) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const {
    state,
    lang,
    setLang,
    startListening,
    stopListening,
    confirmReservation,
    reset,
  } = useVoicePublicReservation(clientId);

  const handleOpen = () => {
    reset();
    setPhone("");
    setEmail("");
    setOpen(true);
  };
  const handleClose = () => {
    reset();
    setPhone("");
    setEmail("");
    setOpen(false);
  };

  const renderContent = () => {
    switch (state.status) {
      case "idle":
        return (
          <div className="vpr-idle">
            <div className="vpr-lang-toggle">
              <button
                className={`vpr-lang-btn ${lang === "bcms" ? "active" : ""}`}
                onClick={() => setLang("bcms")}
              >
                SRB/BA/HR
              </button>
              <button
                className={`vpr-lang-btn ${lang === "sl" ? "active" : ""}`}
                onClick={() => setLang("sl")}
              >
                SLO
              </button>
              <button
                className={`vpr-lang-btn ${lang === "en" ? "active" : ""}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
            </div>
            <div className="vpr-examples">
              <p>Say something like:</p>
              <ul>
                <li>"Rezervacija za Ana Petrović sutra u 20:00 za 4 osobe"</li>
                <li>"Reserve for John Smith April 15th at 7pm for 2 people"</li>
                <li>
                  "Rezerviraj za Marko Novak večeras u 19:30 za troje, broj
                  0641234567"
                </li>
              </ul>
            </div>
            <button className="vpr-mic-btn" onClick={startListening}>
              <AudioOutlined /> Start Speaking
            </button>
          </div>
        );

      case "listening": {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        return (
          <div className="vpr-listening">
            <div className="vpr-lang-toggle">
              <button
                className={`vpr-lang-btn ${lang === "bcms" ? "active" : ""}`}
                onClick={() => setLang("bcms")}
              >
                SRB/BA/HR
              </button>
              <button
                className={`vpr-lang-btn ${lang === "sl" ? "active" : ""}`}
                onClick={() => setLang("sl")}
              >
                SLO
              </button>
              <button
                className={`vpr-lang-btn ${lang === "en" ? "active" : ""}`}
                onClick={() => setLang("en")}
              >
                EN
              </button>
            </div>
            <div className="vpr-pulse-ring">
              <div className="vpr-pulse-dot">
                <AudioOutlined />
              </div>
            </div>
            <p className="vpr-listening-label">
              {isIOS
                ? "Speak now — processing will start automatically"
                : "Listening… speak then press Done"}
            </p>
            <div className="vpr-live-transcript">{state.transcript || "…"}</div>
            {!isIOS && (
              <Button
                type="primary"
                size="large"
                className="vpr-done-btn"
                onClick={stopListening}
              >
                Done
              </Button>
            )}
          </div>
        );
      }

      case "parsing":
        return (
          <div className="vpr-loading">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Processing: "{state.transcript}"</p>
          </div>
        );

      case "preview": {
        const d = state.data;
        const dateLabel = (() => {
          try {
            return new Date(d.date + "T12:00:00").toLocaleDateString("en-GB", {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            });
          } catch {
            return d.date;
          }
        })();
        const resolvedPhone = phone || d.guestPhone || "";
        return (
          <div className="vpr-preview">
            <div className="vpr-preview-hero">
              <div className="vpr-preview-label">New Reservation</div>
              <div className="vpr-preview-name">{d.guestName}</div>
            </div>
            <div className="vpr-preview-rows">
              <div className="vpr-preview-row">
                <CalendarOutlined />
                <span>{dateLabel}</span>
              </div>
              <div className="vpr-preview-row">
                <ClockCircleOutlined />
                <span>{d.time}</span>
              </div>
              <div className="vpr-preview-row">
                <TeamOutlined />
                <span>
                  {d.numberOfGuests}{" "}
                  {d.numberOfGuests === 1 ? "guest" : "guests"}
                </span>
              </div>
              {d.note && (
                <div className="vpr-preview-row">
                  📝<span>{d.note}</span>
                </div>
              )}
            </div>
            <div className="vpr-field">
              <label>
                <PhoneOutlined /> Phone number *
              </label>
              <Input
                placeholder="+381 6x xxx xxxx"
                value={resolvedPhone}
                onChange={(e) => setPhone(e.target.value)}
                size="large"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
              />
            </div>
            <div className="vpr-field">
              <label>Email (optional)</label>
              <Input
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size="large"
                type="email"
                inputMode="email"
                autoComplete="email"
              />
            </div>
            <div className="vpr-actions">
              <Button icon={<CloseOutlined />} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                icon={<RedoOutlined />}
                onClick={() => {
                  setPhone("");
                  setEmail("");
                  startListening();
                }}
              >
                Record Again
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={!resolvedPhone.trim()}
                onClick={() =>
                  confirmReservation(d, resolvedPhone, email || undefined)
                }
              >
                Confirm
              </Button>
            </div>
          </div>
        );
      }

      case "submitting":
        return (
          <div className="vpr-loading">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />}
            />
            <p>Creating your reservation…</p>
          </div>
        );

      case "success":
        return (
          <div className="vpr-success">
            <div className="vpr-success-icon">✅</div>
            <h3>Reservation Confirmed!</h3>
            <p>
              <strong>{state.guestName}</strong>
            </p>
            <p>
              {new Date(state.date + "T12:00:00").toLocaleDateString("en-GB", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}{" "}
              at {state.time}
            </p>
            <Button
              type="primary"
              size="large"
              block
              onClick={handleClose}
              style={{ marginTop: 16 }}
            >
              Done
            </Button>
          </div>
        );

      case "error":
        return (
          <div className="vpr-error">
            <Alert type="error" showIcon message={state.message} />
            <div className="vpr-actions" style={{ marginTop: 16 }}>
              <Button icon={<CloseOutlined />} onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<AudioOutlined />}
                onClick={() => {
                  reset();
                  startListening();
                }}
              >
                Try Again
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {/* Floating voice button */}
      <button
        className="vpr-fab"
        onClick={handleOpen}
        aria-label="Voice reservation"
      >
        <AudioOutlined />
        <span>Voice Booking</span>
      </button>

      <Modal
        open={open}
        title="🎙️ Voice Reservation"
        footer={null}
        onCancel={handleClose}
        width={440}
        style={{ maxWidth: "100vw" }}
        destroyOnClose
      >
        {renderContent()}
      </Modal>
    </>
  );
}
